import {
  Dispatch,
  MutableRefObject,
  ReactNode,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
} from 'react'
import { Card, CardContent, CardHeader, CardTitle } from 'shared/ui/card'
import { Input } from 'shared/ui/input'
import { Label } from 'shared/ui/label'
import { Textarea } from 'shared/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'shared/ui/select'
import { AlertTriangle, Building2, FileText, User } from 'lucide-react'
import type {
  PermitType,
  TemplateField,
  TemplateFieldDatasourceItem,
} from 'shared/api/templateService'
import type { ParsedFieldConfig } from '../utils/templateFieldUtils'
import { DatePicker } from '../../DatePicker'
import { SearchSelectField } from '../../SearchSelectField'
import { PermitTypeSelect } from './components/PermitTypeSelect'
import { PermitTimeField } from './components/PermitTimeField'
import { cn } from 'shared/ui/utils'
import { buildTemplateFieldValidationKey } from 'modules/permit/pages/permit-request/utils/validationTargets'

type TimeParts = {
  hour: string
  minute: string
}

type ControllerDependencyInfo = {
  params: Record<string, string>
  dependencyMissing: boolean
  config?: ParsedFieldConfig | null
} | null

interface GeneralDetailsSectionProps {
  isReadOnly?: boolean
  isEditMode?: boolean
  permitData: any
  setPermitData: Dispatch<SetStateAction<any>>
  currentUser?: any
  permitTypes: PermitType[]
  isPermitTypeLoading?: boolean
  permitTypeError?: string | null
  onPermitTypeChange?: (permit: PermitType | null) => void
  templateError?: string | null
  isTemplateLoading?: boolean
  isGeneralEnabled: boolean
  generalFields: TemplateField[]
  generalFieldMap: Record<string, TemplateField | undefined>
  renderTemplateFieldInput: (field: TemplateField, options?: { disabled?: boolean }) => ReactNode
  unitField?: TemplateField | null
  workLocationFieldId: string
  unitFieldId: string
  dependencyCacheRef: MutableRefObject<Record<string, string | undefined>>
  setFieldOptions: Dispatch<SetStateAction<Record<string, TemplateFieldDatasourceItem[]>>>
  getFieldOptions: (fieldId: string) => TemplateFieldDatasourceItem[]
  isFieldLoading: (fieldId: string) => boolean
  handleStartDateChange: (value: string) => void
  handleEndDateChange: (value: string) => void
  dateErrors: { startDate: string; endDate: string; dateRange: string }
  startTimeParts: TimeParts
  endTimeParts: TimeParts
  handleTimeHourChange: (key: 'startTime' | 'endTime', value: string) => void
  handleTimeMinuteChange: (key: 'startTime' | 'endTime', value: string) => void
  clearTimeField: (key: 'startTime' | 'endTime') => void
  timeHourOptions: string[]
  timeMinuteOptions: string[]
  controllerField?: TemplateField | null
  controllerDependencyInfo: ControllerDependencyInfo
  controllerDependencyLabel?: string
  tenantId: string
  canApproveCurrentStage: boolean
  onGeneralFieldLabelChange: (fieldCode: string, label: string) => void
  generalFieldLabels?: Record<string, string>
  getValidationContainerClass?: (key?: string | null) => string
}

/**
 * GeneralDetailsSection centralises the monolithic "General Data" card so other teams
 * can reason about permit basics (dates, times, location, controller, etc.) without
 * scrolling through the entire PermitRequestForm component.
 */
export function GeneralDetailsSection({
  isReadOnly,
  isEditMode,
  permitData,
  setPermitData,
  currentUser,
  permitTypes,
  isPermitTypeLoading,
  permitTypeError,
  onPermitTypeChange,
  templateError,
  isTemplateLoading,
  isGeneralEnabled,
  generalFields,
  generalFieldMap,
  renderTemplateFieldInput,
  unitField,
  workLocationFieldId,
  unitFieldId,
  dependencyCacheRef,
  setFieldOptions,
  getFieldOptions,
  isFieldLoading,
  handleStartDateChange,
  handleEndDateChange,
  dateErrors,
  startTimeParts,
  endTimeParts,
  handleTimeHourChange,
  handleTimeMinuteChange,
  clearTimeField,
  timeHourOptions,
  timeMinuteOptions,
  controllerField,
  controllerDependencyInfo,
  controllerDependencyLabel,
  tenantId,
  canApproveCurrentStage,
  onGeneralFieldLabelChange,
  generalFieldLabels = {},
  getValidationContainerClass,
}: GeneralDetailsSectionProps) {
  const emptyDependencyParams = useMemo<Record<string, string | undefined>>(() => ({}), [])
  const isGeneralSectionDisabled = Boolean(isReadOnly || canApproveCurrentStage)
  const isGuidLike = useCallback((candidate?: string | null) => {
    const normalized = (candidate ?? '').trim()
    if (!normalized) {
      return false
    }
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(normalized)
  }, [])
  const normalizeOptionValue = useCallback((candidate?: string | null) => {
    return (candidate ?? '').trim().toLowerCase()
  }, [])

  const deriveOptionLabel = useCallback(
    (option: TemplateFieldDatasourceItem, fallbackValue?: string): string => {
      const rawValue = (option.value ?? fallbackValue ?? '').trim()
      const rawLabel = (option.label ?? '').trim()
      if (rawLabel && rawLabel.toLowerCase() !== rawValue.toLowerCase() && !isGuidLike(rawLabel)) {
        return rawLabel
      }

      if (option.columns && typeof option.columns === 'object') {
        const descriptive = Object.values(option.columns)
          .map((value) => (value === null || value === undefined ? '' : String(value).trim()))
          .find((value) => value && value.toLowerCase() !== rawValue.toLowerCase() && !isGuidLike(value))
        if (descriptive) {
          return descriptive
        }
      }

      if (rawLabel) {
        return rawLabel
      }

      return rawValue
    },
    [isGuidLike],
  )

  const WORK_LOCATION_FIELD_CODE = 'WORKLOCATION'
  const UNIT_FIELD_CODE = 'UNIT'
  const resolveValidationKey = useCallback((field?: TemplateField | null) => buildTemplateFieldValidationKey(field), [])
  const resolveValidationClassName = useCallback(
    (field?: TemplateField | null) => getValidationContainerClass?.(resolveValidationKey(field)) ?? '',
    [getValidationContainerClass, resolveValidationKey],
  )
  const resolveColumnSpanClass = (field?: TemplateField | null): string => {
    const layout = typeof field?.layout === 'number' ? field.layout : null
    return layout === 1 ? 'md:col-span-2' : 'md:col-span-1'
  }
  useEffect(() => {
    if (!isEditMode && currentUser) {
      console.debug('[permit][general-section] currentUser (create mode)', {
        company: currentUser.company,
        companyD: currentUser.companyD,
        companyId: currentUser.companyId,
        raw: currentUser,
      })
    }
  }, [isEditMode, currentUser])

  const resolveOptionLabel = useCallback(
    (fieldId: string, value?: string | null) => {
      if (!fieldId || !value) {
        return ''
      }
      const options = getFieldOptions(fieldId)
      const normalizedValue = normalizeOptionValue(value)
      const match = options.find(
        (option) => normalizeOptionValue(option.value) === normalizedValue,
      )
      if (!match) {
        return ''
      }
      const derived = deriveOptionLabel(match, value).trim()
      if (!derived) {
        return ''
      }
      if (derived.toLowerCase() === value.trim().toLowerCase()) {
        return ''
      }
      if (isGuidLike(derived)) {
        return ''
      }
      return derived
    },
    [deriveOptionLabel, getFieldOptions, isGuidLike],
  )

  const buildSelectOptions = useCallback(
    (fieldId: string, value?: string | null, fallbackLabel?: string) => {
      const options = getFieldOptions(fieldId).map((option) => ({
        ...option,
        label: deriveOptionLabel(option),
      }))
      if (!value || value === '_default') {
        return options
      }
      const normalizedValue = normalizeOptionValue(value)
      const exists = options.some(
        (option) => normalizeOptionValue(option.value) === normalizedValue,
      )
      if (exists) {
        return options
      }
      const normalizedFallback = (fallbackLabel ?? '').trim()
      return [{ value, label: normalizedFallback || value }, ...options]
    },
    [deriveOptionLabel, getFieldOptions, normalizeOptionValue],
  )

  useEffect(() => {
    if (!permitData.workLocation) {
      onGeneralFieldLabelChange(WORK_LOCATION_FIELD_CODE, '')
      return
    }
    const label = resolveOptionLabel(workLocationFieldId, permitData.workLocation)
    if (label) {
      onGeneralFieldLabelChange(WORK_LOCATION_FIELD_CODE, label)
    }
  }, [
    onGeneralFieldLabelChange,
    permitData.workLocation,
    resolveOptionLabel,
    workLocationFieldId,
  ])

  useEffect(() => {
    if (!permitData.unit) {
      onGeneralFieldLabelChange(UNIT_FIELD_CODE, '')
      return
    }
    const label = resolveOptionLabel(unitFieldId, permitData.unit)
    if (label) {
      onGeneralFieldLabelChange(UNIT_FIELD_CODE, label)
    }
  }, [onGeneralFieldLabelChange, permitData.unit, resolveOptionLabel, unitFieldId])
  const renderDateInputs = () => (
    <>
      {(generalFieldMap.STDATE || generalFieldMap.ENDATE) && (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {generalFieldMap.STDATE ? (
            <div
              data-validation-key={resolveValidationKey(generalFieldMap.STDATE) ?? undefined}
              className={resolveValidationClassName(generalFieldMap.STDATE)}
            >
              <DatePicker
                id="start-date"
                label={generalFieldMap.STDATE?.label || 'Work Date (Start)'}
                required={Boolean(generalFieldMap.STDATE?.required)}
                value={permitData.startDate}
                onChange={handleStartDateChange}
                error={dateErrors.startDate}
                disabled={isGeneralSectionDisabled}
              />
            </div>
          ) : null}
          {generalFieldMap.ENDATE ? (
            <div
              data-validation-key={resolveValidationKey(generalFieldMap.ENDATE) ?? undefined}
              className={resolveValidationClassName(generalFieldMap.ENDATE)}
            >
              <DatePicker
                id="end-date"
                label={generalFieldMap.ENDATE?.label || 'Work Date (End)'}
                required={Boolean(generalFieldMap.ENDATE?.required)}
                value={permitData.endDate}
                onChange={handleEndDateChange}
                error={dateErrors.endDate}
                disabled={isGeneralSectionDisabled}
              />
            </div>
          ) : null}
        </div>
      )}
      {dateErrors.dateRange && (generalFieldMap.STDATE || generalFieldMap.ENDATE) ? (
        <div className="flex items-center gap-2 rounded-md border border-red-200 bg-red-50 p-3">
          <AlertTriangle className="h-4 w-4 text-red-500" />
          <span className="text-sm text-red-700">{dateErrors.dateRange}</span>
        </div>
      ) : null}
    </>
  )

  const renderControllerField = () => {
    if (!controllerField) {
      return null
    }
    return (
      <SearchSelectField
        field={controllerField}
        value={permitData.controller}
        onChange={(selection) => setPermitData((prev: any) => ({ ...prev, controller: selection }))}
        tenantId={tenantId}
        dependencyParams={controllerDependencyInfo?.params ?? emptyDependencyParams}
        dependencyMissing={
          controllerDependencyInfo?.config?.dependsOn
            ? controllerDependencyInfo?.dependencyMissing ?? false
            : false
        }
        dependencyLabel={controllerDependencyLabel}
        disabled={isGeneralSectionDisabled}
      />
    )
  }

  const orderedGeneralFields = (() => {
    const nodes: ReactNode[] = []
    let renderedWorkDescription = false
    let renderedWorkLocation = false
    let renderedDates = false
    let renderedTimes = false
    let renderedController = false

    generalFields.forEach((field, index) => {
      const code = (field.code ?? '').trim().toUpperCase()

      if (code === 'WORKDESCR') {
        if (renderedWorkDescription) {
          return
        }
        renderedWorkDescription = true
        nodes.push(
          <div
            key={field.fieldId ?? 'WORKDESCR'}
            className={cn(resolveColumnSpanClass(field), resolveValidationClassName(field))}
            data-validation-key={resolveValidationKey(field) ?? undefined}
          >
            <div className="space-y-2">
              <Label htmlFor="work-description" className="text-sm text-gray-600">
                {field.label || 'Work Description'}
                {field.required ? <span className="text-red-500">*</span> : null}
              </Label>
              <Textarea
                id="work-description"
                value={permitData.workDescription}
                onChange={(event) =>
                  setPermitData((prev: any) => ({
                    ...prev,
                    workDescription: event.target.value,
                  }))
                }
                className="min-h-[80px] border-gray-200 bg-gray-50"
                disabled={isGeneralSectionDisabled}
              />
            </div>
          </div>,
        )
        return
      }

      if (code === WORK_LOCATION_FIELD_CODE || code === UNIT_FIELD_CODE) {
        if (renderedWorkLocation) {
          return
        }
        renderedWorkLocation = true
        if (!(generalFieldMap.WORKLOCATION || generalFieldMap.UNIT)) {
          return
        }
        nodes.push(
          <div key="work-location-unit" className="md:col-span-2">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              {generalFieldMap.WORKLOCATION ? (
                <div
                  className={cn('space-y-2', resolveValidationClassName(generalFieldMap.WORKLOCATION))}
                  data-validation-key={resolveValidationKey(generalFieldMap.WORKLOCATION) ?? undefined}
                >
                  <SearchSelectField
                    field={generalFieldMap.WORKLOCATION}
                    tenantId={tenantId}
                    value={
                      permitData.workLocation
                        ? {
                            value: permitData.workLocation,
                            label:
                              ((generalFieldLabels[WORK_LOCATION_FIELD_CODE]?.trim() &&
                                !isGuidLike(generalFieldLabels[WORK_LOCATION_FIELD_CODE]?.trim()))
                                ? generalFieldLabels[WORK_LOCATION_FIELD_CODE]?.trim()
                                : '') ||
                              resolveOptionLabel(workLocationFieldId, permitData.workLocation) ||
                              permitData.workLocation,
                          }
                        : null
                    }
                    disabled={isGeneralSectionDisabled}
                    onChange={(selectionValue) => {
                      const nextValue = selectionValue?.value?.trim() ?? ''
                      if (!nextValue) {
                        setPermitData((prev: any) => ({
                          ...prev,
                          workLocation: '',
                          unit: '',
                        }))
                        onGeneralFieldLabelChange(WORK_LOCATION_FIELD_CODE, '')
                        onGeneralFieldLabelChange(UNIT_FIELD_CODE, '')
                        if (unitField) {
                          dependencyCacheRef.current[unitFieldId] = undefined
                          setFieldOptions((prev) => ({
                            ...prev,
                            [unitFieldId]: [],
                          }))
                        }
                        return
                      }
                      const resolvedLabel =
                        selectionValue?.label?.trim() ||
                        resolveOptionLabel(workLocationFieldId, nextValue) ||
                        nextValue
                      const sameWorkLocation =
                        normalizeOptionValue(permitData.workLocation) === normalizeOptionValue(nextValue)
                      setPermitData((prev: any) => ({
                        ...prev,
                        workLocation: nextValue,
                        unit: sameWorkLocation ? prev.unit : '',
                      }))
                      onGeneralFieldLabelChange(WORK_LOCATION_FIELD_CODE, resolvedLabel)
                      if (!sameWorkLocation) {
                        onGeneralFieldLabelChange(UNIT_FIELD_CODE, '')
                      }
                    }}
                  />
                  {isFieldLoading(workLocationFieldId) &&
                  !permitData.workLocation &&
                  getFieldOptions(workLocationFieldId).length === 0 ? (
                    <p className="text-xs text-gray-500">Loading options...</p>
                  ) : null}
                </div>
              ) : null}

              {generalFieldMap.UNIT ? (
                <div
                  className={cn('space-y-2', resolveValidationClassName(generalFieldMap.UNIT))}
                  data-validation-key={resolveValidationKey(generalFieldMap.UNIT) ?? undefined}
                >
                  <Label className="text-sm text-gray-600">
                    {generalFieldMap.UNIT?.label || 'Unit'}
                    {generalFieldMap.UNIT?.required ? (
                      <span className="text-red-500">*</span>
                    ) : null}
                  </Label>
                  <Select
                    value={permitData.unit || '_default'}
                    onValueChange={(value) => {
                      const nextValue = value === '_default' ? '' : value
                      setPermitData((prev: any) => ({
                        ...prev,
                        unit: nextValue,
                      }))
                      if (!nextValue) {
                        onGeneralFieldLabelChange(UNIT_FIELD_CODE, '')
                        return
                      }
                      const resolvedLabel = resolveOptionLabel(unitFieldId, nextValue)
                      if (resolvedLabel) {
                        onGeneralFieldLabelChange(UNIT_FIELD_CODE, resolvedLabel)
                      }
                    }}
                    disabled={
                      isGeneralSectionDisabled ||
                      isFieldLoading(unitFieldId)
                    }
                  >
                    <SelectTrigger className="border-gray-200 bg-gray-50">
                      <SelectValue placeholder="Select unit" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_default">Select unit</SelectItem>
                      {buildSelectOptions(
                        unitFieldId,
                        permitData.unit,
                        generalFieldLabels[UNIT_FIELD_CODE],
                      ).map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : null}
            </div>
          </div>,
        )
        return
      }

      if (code === 'STDATE' || code === 'ENDATE') {
        if (renderedDates) {
          return
        }
        renderedDates = true
        if (!(generalFieldMap.STDATE || generalFieldMap.ENDATE)) {
          return
        }
        nodes.push(
          <div key="work-dates" className="md:col-span-2">
            {renderDateInputs()}
          </div>,
        )
        return
      }

      if (code === 'STTIME' || code === 'ENTIME') {
        if (renderedTimes) {
          return
        }
        renderedTimes = true
        if (!(generalFieldMap.STTIME || generalFieldMap.ENTIME)) {
          return
        }
        nodes.push(
          <div key="work-times" className="md:col-span-2">
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                {generalFieldMap.STTIME ? (
                  <div
                    data-validation-key={resolveValidationKey(generalFieldMap.STTIME) ?? undefined}
                    className={resolveValidationClassName(generalFieldMap.STTIME)}
                  >
                    <PermitTimeField
                      label={generalFieldMap.STTIME?.label || 'Start Time'}
                      fieldKey="startTime"
                      parts={startTimeParts}
                      generalFieldMap={generalFieldMap}
                      handleTimeHourChange={handleTimeHourChange}
                      handleTimeMinuteChange={handleTimeMinuteChange}
                      clearTimeField={clearTimeField}
                      timeHourOptions={timeHourOptions}
                      timeMinuteOptions={timeMinuteOptions}
                      hasValue={Boolean(permitData.startTime)}
                      disabled={isGeneralSectionDisabled}
                    />
                  </div>
                ) : null}
                {generalFieldMap.ENTIME ? (
                  <div
                    data-validation-key={resolveValidationKey(generalFieldMap.ENTIME) ?? undefined}
                    className={resolveValidationClassName(generalFieldMap.ENTIME)}
                  >
                    <PermitTimeField
                      label={generalFieldMap.ENTIME?.label || 'End Time'}
                      fieldKey="endTime"
                      parts={endTimeParts}
                      generalFieldMap={generalFieldMap}
                      handleTimeHourChange={handleTimeHourChange}
                      handleTimeMinuteChange={handleTimeMinuteChange}
                      clearTimeField={clearTimeField}
                      timeHourOptions={timeHourOptions}
                      timeMinuteOptions={timeMinuteOptions}
                      hasValue={Boolean(permitData.endTime)}
                      disabled={isGeneralSectionDisabled}
                    />
                  </div>
                ) : null}
              </div>
            </div>
          </div>,
        )
        return
      }

      if (code === 'CONTROLLER') {
        if (renderedController) {
          return
        }
        renderedController = true
        const controllerNode = renderControllerField()
        if (!controllerNode) {
          return
        }
        nodes.push(
          <div
            key={field.fieldId ?? 'CONTROLLER'}
            className={cn(resolveColumnSpanClass(field), resolveValidationClassName(field))}
            data-validation-key={resolveValidationKey(field) ?? undefined}
          >
            {controllerNode}
          </div>,
        )
        return
      }

      const node = renderTemplateFieldInput(field, { disabled: isGeneralSectionDisabled })
      if (!node) {
        return
      }
      nodes.push(
        <div
          key={field.fieldId ?? field.code ?? `general-${index}`}
          className={cn(resolveColumnSpanClass(field), resolveValidationClassName(field))}
          data-validation-key={resolveValidationKey(field) ?? undefined}
        >
          {node}
        </div>,
      )
    })

    return nodes
  })()

  return (
    <>
      {isReadOnly ? (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          แบบฟอร์มอ่านอย่างเดียว ผู้ใช้งานจึงไม่สามารถปรับข้อมูลชุดนี้ได้
        </div>
      ) : null}

      <fieldset disabled={isGeneralSectionDisabled} className="space-y-6 border-0 p-0">
        <Card className="shadow-sm">
          <CardHeader className="pb-6">
            <CardTitle className="flex items-center gap-2 text-lg">
              <FileText className="h-5 w-5 text-orange-500" />
              Permit Header
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="permit-no" className="text-sm text-gray-600">
                Permit Number
              </Label>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <Input
                  id="permit-no"
                  value={permitData.permitNo || ''}
                  readOnly
                  className="cursor-not-allowed border-gray-200 bg-gray-100 text-gray-800"
                  placeholder="สร้างอัตโนมัติเมื่อบันทึกครั้งแรก"
                />
              </div>
              <p className="text-xs text-gray-500">
                ระบบจะสร้างเลขที่ Permit ให้อัตโนมัติเมื่อบันทึกครั้งแรก
              </p>
            </div>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="requestor-name" className="text-sm text-gray-600">
                  Document Creator Name
                </Label>
                <div className="relative">
                  <Input
                    id="requestor-name"
                    value={permitData.requestorName || currentUser?.name || ''}
                    readOnly
                    disabled
                    className="cursor-not-allowed border-gray-200 bg-gray-100 pl-10 text-gray-800"
                  />
                  <User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transform text-gray-400" />
                </div>
                <p className="text-xs text-gray-500">
                  Auto-filled from the signed-in user.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="requestor-company" className="text-sm text-gray-600">
                  Company Name
                </Label>
                <div className="relative">
                  <Input
                    id="requestor-company"
                    value={permitData.requestorCompany || currentUser?.company || ''}
                    readOnly
                    disabled
                    className="cursor-not-allowed border-gray-200 bg-gray-100 pl-10 text-gray-800"
                  />
                  <Building2 className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transform text-gray-400" />
                </div>
                <p className="text-xs text-gray-500">Captured for audit and filtering.</p>
              </div>
            </div>

            <PermitTypeSelect
              permitData={permitData}
              setPermitData={setPermitData}
              permitTypes={permitTypes}
              isPermitTypeLoading={isPermitTypeLoading}
              isEditMode={isEditMode}
              permitTypeError={permitTypeError}
              onPermitTypeChange={onPermitTypeChange}
            />
          </CardContent>
        </Card>

        {isGeneralEnabled ? (
          <Card className="shadow-sm">
            <CardHeader className="pb-6">
              <CardTitle className="flex items-center gap-2 text-lg">
                <FileText className="h-5 w-5 text-blue-500" />
                General Data
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {templateError ? (
                <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                  {templateError}
                </div>
              ) : isTemplateLoading ? (
                <div className="rounded-md border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
                  Loading template details...
                </div>
              ) : generalFields.length === 0 ? (
                <div className="rounded-md border border-dashed border-gray-300 bg-gray-50 p-6 text-center text-sm text-gray-600">
                  Select a permit type to continue.
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 gap-6 md:grid-cols-2">{orderedGeneralFields}</div>
                </>
              )}
            </CardContent>
          </Card>
        ) : null}
      </fieldset>
    </>
  )
}
